import type { ComponentProps } from 'react'
import * as React from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { ChevronRightIcon, HomeIcon, MoreHorizontalIcon } from 'lucide-react'
import Link from 'next/link'
import { Slot } from 'radix-ui'

const Breadcrumb = React.forwardRef<
	HTMLElement,
	React.ComponentPropsWithoutRef<'nav'> & {
		separator?: React.ReactNode
	}
>(({ ...props }, ref) => (
	<nav
		aria-label='breadcrumb'
		ref={ref}
		{...props}
	/>
))

Breadcrumb.displayName = 'Breadcrumb'

const BreadcrumbList = React.forwardRef<
	HTMLOListElement,
	React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
	<ol
		className={cn(
			'flex flex-wrap items-center gap-1.5 text-sm font-semibold wrap-break-word sm:gap-2.5',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

BreadcrumbList.displayName = 'BreadcrumbList'

const BreadcrumbItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
	<li
		className={cn('inline-flex h-[32px] items-center gap-1.5', className)}
		ref={ref}
		{...props}
	/>
))

BreadcrumbItem.displayName = 'BreadcrumbItem'

const BreadcrumbLink = React.forwardRef<
	HTMLAnchorElement,
	React.ComponentPropsWithoutRef<typeof Link> & {
		asChild?: boolean
	}
>(({ asChild, className, ...props }, ref) => {
	const Comp = asChild ? Slot.Root : Link

	return (
		<Comp
			className={cn(
				'bg-card hover:text-primary flex h-full flex-row items-center justify-center rounded px-4 shadow-xs transition-colors',
				className,
			)}
			ref={ref}
			{...props}
		/>
	)
})

BreadcrumbLink.displayName = 'BreadcrumbLink'

const BreadcrumbPage = React.forwardRef<
	HTMLSpanElement,
	React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
	<span
		aria-current='page'
		aria-disabled='true'
		className={cn(
			'bg-card text-foreground flex h-full flex-row items-center justify-center rounded px-4 shadow-xs',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

BreadcrumbPage.displayName = 'BreadcrumbPage'

const BreadcrumbSeparator = ({
	children,
	className,
	...props
}: React.ComponentProps<'li'>) => (
	<li
		aria-hidden='true'
		className={cn('[&>svg]:size-3.5!', className)}
		role='presentation'
		{...props}
	>
		{children || <ChevronRightIcon />}
	</li>
)

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

const BreadcrumbEllipsis = ({
	className,
	...props
}: React.ComponentProps<'span'>) => (
	<span
		aria-hidden='true'
		className={cn(
			'bg-card flex h-full w-9 flex-row items-center justify-center rounded',
			className,
		)}
		role='presentation'
		{...props}
	>
		<MoreHorizontalIcon />
		<span className='sr-only'>More</span>
	</span>
)

BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis'

const HomeBreadcrumbItem = ({
	className,
	...props
}: Omit<ComponentProps<typeof BreadcrumbLink>, 'href'>) => (
	<BreadcrumbItem>
		<BreadcrumbLink
			{...props}
			className={cn('w-[32px] px-0', className)}
			href='/dashboard'
		>
			<HomeIcon size={14} />
		</BreadcrumbLink>
	</BreadcrumbItem>
)

HomeBreadcrumbItem.displayName = 'HomeBreadcrumbItem'

export {
	Breadcrumb,
	BreadcrumbEllipsis,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
	HomeBreadcrumbItem,
}
